"use client";

import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, PhoneIncoming, Video, VideoOff, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage, getInitials } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  initiateCall,
  answerCall,
  declineCall,
  endCall,
  subscribeCall,
  isCallExpired,
  markCallMissed,
  type CallDoc,
} from '@/services/calls';
import { sendCallTrace } from '@/services/chat';
import {
  startRingtone,
  stopRingtone,
  type RingtonePrefs,
  DEFAULT_RINGTONE_PREFS,
} from '@/services/ringtone';

// ─── Props ───────────────────────────────────────────────────────────────────

interface CallControlsProps {
  currentUserId: string;
  currentUserName: string;
  currentUserAvatar?: string;
  recipientId: string;
  recipientName: string;
  recipientAvatar?: string;
  roomId?: string;        // chat room to post call-trace messages into
  ringtonePrefs?: RingtonePrefs;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CallControls({
  currentUserId,
  currentUserName,
  currentUserAvatar,
  recipientId,
  recipientName,
  recipientAvatar,
  roomId,
  ringtonePrefs,
}: CallControlsProps) {
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [callDoc, setCallDoc] = useState<CallDoc | null>(null);
  const [duration, setDuration] = useState('0:00');
  const [micMuted, setMicMuted] = useState(false);
  const [camMuted, setCamMuted] = useState(false);
  const prevStatusRef = useRef<string | null>(null);
  const traceSentRef = useRef<Set<string>>(new Set()); // prevents double-posting traces

  // ── Post a call-trace message into the chat room ──────────────────────────
  const postTrace = useCallback(async (
    call: CallDoc,
    status: 'ended' | 'missed' | 'declined' | 'cancelled',
    durationSeconds?: number,
  ) => {
    const traceRoomId = call.roomId || roomId;
    if (!traceRoomId) return;
    const traceKey = `${call.id}-${status}`;
    if (traceSentRef.current.has(traceKey)) return;
    traceSentRef.current.add(traceKey);
    try {
      await sendCallTrace(
        traceRoomId,
        currentUserId,
        currentUserName,
        currentUserAvatar,
        { callType: call.type, callStatus: status, durationSeconds, callerId: call.callerId },
      );
    } catch (err) {
      console.error('Failed to post call trace:', err);
    }
  }, [roomId, currentUserId, currentUserName, currentUserAvatar]);

  // ── Subscribe to call doc changes ──────────────────────────────────────────
  useEffect(() => {
    if (!activeCallId) {
      setCallDoc(null);
      return;
    }
    const unsub = subscribeCall(activeCallId, (updatedDoc) => {
      setCallDoc(updatedDoc);
      if (!updatedDoc) return;

      const prev = prevStatusRef.current;
      const next = updatedDoc.status;

      // Remote terminal transition — the other party caused this state change.
      // Post trace on the CALLER's side for missed/declined outcomes.
      if (prev !== next && (next === 'missed' || next === 'declined' || next === 'ended')) {
        if (next === 'missed' && updatedDoc.callerId === currentUserId) {
          // Caller sees: receiver never picked up
          postTrace(updatedDoc, 'missed');
        } else if (next === 'declined' && updatedDoc.callerId === currentUserId) {
          // Caller sees: receiver explicitly declined
          postTrace(updatedDoc, 'declined');
        } else if (next === 'ended' && updatedDoc.endedBy !== currentUserId) {
          // Other party ended the active call → post trace with duration
          const answeredMs = updatedDoc.answeredAt?.toMillis?.() || Date.now();
          const endedMs = updatedDoc.endedAt?.toMillis?.() || Date.now();
          const durationSeconds = Math.max(0, Math.floor((endedMs - answeredMs) / 1000));
          postTrace(updatedDoc, 'ended', durationSeconds);
        }
      }

      prevStatusRef.current = next;

      // Clear the call UI after a short delay so the user sees the final status
      if (next === 'ended' || next === 'declined' || next === 'missed') {
        setTimeout(() => {
          setActiveCallId(null);
          setCallDoc(null);
          setDuration('0:00');
          prevStatusRef.current = null;
        }, 2000);
      }
    });
    return unsub;
  }, [activeCallId, currentUserId, postTrace]);

  // ── Ringtone: play while ringing (caller hears ring-back) ──────────────────
  useEffect(() => {
    const status = callDoc?.status ?? null;
    if (status === 'ringing' && callDoc?.callerId === currentUserId) {
      // Caller hears ring-back tone
      startRingtone(ringtonePrefs ?? DEFAULT_RINGTONE_PREFS);
    } else if (prevStatusRef.current === 'ringing') {
      stopRingtone();
    }
    prevStatusRef.current = status;
    return () => { stopRingtone(); };
  }, [callDoc?.status, callDoc?.callerId, currentUserId, ringtonePrefs]);

  // ── Auto-expire stale ringing calls ────────────────────────────────────────
  useEffect(() => {
    if (!callDoc || callDoc.status !== 'ringing') return;
    const interval = setInterval(() => {
      if (isCallExpired(callDoc, 30000)) {
        markCallMissed(callDoc.id);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [callDoc]);

  // ── Timer for active call duration ─────────────────────────────────────────
  useEffect(() => {
    if (!callDoc || callDoc.status !== 'active') return;
    const answeredTime = callDoc.answeredAt?.toMillis?.() || Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - answeredTime) / 1000);
      const m = Math.floor(elapsed / 60);
      const s = elapsed % 60;
      setDuration(`${m}:${s.toString().padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [callDoc?.status, callDoc?.answeredAt]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleStartCall = useCallback(async (type: 'audio' | 'video') => {
    try {
      const callId = await initiateCall({
        callerId: currentUserId,
        callerName: currentUserName,
        callerAvatar: currentUserAvatar,
        receiverId: recipientId,
        receiverName: recipientName,
        receiverAvatar: recipientAvatar,
        type,
        roomId,
      });
      // Register in sessionStorage so app-shell knows this call belongs to the current session
      sessionStorage.setItem('bb-active-call-id', callId);
      setActiveCallId(callId);
      setMicMuted(false);
      setCamMuted(false);
    } catch (err) {
      console.error('Failed to initiate call:', err);
    }
  }, [currentUserId, currentUserName, currentUserAvatar, recipientId, recipientName, recipientAvatar]);

  const handleEndCall = useCallback(async () => {
    stopRingtone();
    if (activeCallId && callDoc) {
      try {
        if (callDoc.status === 'ringing') {
          // Caller cancelled before anyone picked up
          await declineCall(activeCallId, currentUserId);
          await postTrace(callDoc, 'cancelled');
        } else if (callDoc.status === 'active') {
          const answeredMs = callDoc.answeredAt?.toMillis?.() || Date.now();
          const durationSeconds = Math.floor((Date.now() - answeredMs) / 1000);
          await endCall(activeCallId, currentUserId);
          await postTrace(callDoc, 'ended', durationSeconds);
        }
      } catch (err) {
        console.error('Failed to end call:', err);
      }
    }
    setActiveCallId(null);
    setCallDoc(null);
    setDuration('0:00');
  }, [activeCallId, callDoc, currentUserId, postTrace]);

  // ── Idle state: show start-call buttons ────────────────────────────────────
  if (!callDoc) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-green-500/20 hover:text-green-600"
          onClick={() => handleStartCall('audio')}
          title="Audio Call"
        >
          <Phone className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-blue-500/20 hover:text-blue-600"
          onClick={() => handleStartCall('video')}
          title="Video Call"
        >
          <Video className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  // ── Active / Ringing state ─────────────────────────────────────────────────

  const isRinging = callDoc.status === 'ringing';
  const isActive = callDoc.status === 'active';
  const isEnded = callDoc.status === 'ended' || callDoc.status === 'declined' || callDoc.status === 'missed';
  const isVideo = callDoc.type === 'video';

  return (
    <div className="border-b">
      {/* Call bar */}
      <div className={cn(
        "flex items-center gap-2.5 px-3 py-2",
        isRinging && "bg-amber-500/10",
        isActive && "bg-green-500/10",
        isEnded && "bg-muted/50",
      )}>
        {/* Avatar */}
        <Avatar className="h-8 w-8 rounded-full flex-shrink-0">
          <AvatarImage src={recipientAvatar} />
          <AvatarFallback className="text-xs rounded-full">
            {getInitials(recipientName)}
          </AvatarFallback>
        </Avatar>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{recipientName}</p>
          <div className="flex items-center gap-1.5">
            {isRinging && (
              <span className="text-[11px] text-amber-600 dark:text-amber-400 animate-pulse font-medium">
                Calling...
              </span>
            )}
            {isActive && (
              <>
                <span className="text-[11px] text-green-600 dark:text-green-400 font-medium">
                  {duration}
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
              </>
            )}
            {isEnded && (
              <span className="text-[11px] text-muted-foreground">
                {callDoc.status === 'declined' ? 'Declined' : callDoc.status === 'missed' ? 'No answer' : 'Ended'}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground">
              {isVideo ? '• Video' : '• Audio'}
            </span>
          </div>
        </div>

        {/* Control buttons */}
        {!isEnded && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {isActive && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn("h-7 w-7 p-0", micMuted && "bg-destructive/15 text-destructive")}
                  onClick={() => setMicMuted(!micMuted)}
                  title={micMuted ? 'Unmute' : 'Mute'}
                >
                  {micMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                </Button>
                {isVideo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("h-7 w-7 p-0", camMuted && "bg-destructive/15 text-destructive")}
                    onClick={() => setCamMuted(!camMuted)}
                    title={camMuted ? 'Camera on' : 'Camera off'}
                  >
                    {camMuted ? <VideoOff className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </>
            )}
            <Button
              variant="destructive"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleEndCall}
              title="End call"
            >
              <PhoneOff className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Compact video preview (only during active video call) */}
      {isVideo && isActive && (
        <div className="px-3 py-2 border-t bg-black/5 dark:bg-white/5">
          <div className="relative bg-black rounded-md overflow-hidden aspect-[16/9] max-h-32 flex items-center justify-center">
            <div className="text-center text-muted-foreground/60">
              <Video className="h-5 w-5 mx-auto mb-1 opacity-40" />
              <p className="text-[10px]">Video preview</p>
            </div>
            <div className="absolute bottom-1.5 right-1.5 h-8 w-8 rounded border border-white/20 bg-card overflow-hidden">
              <Avatar className="h-8 w-8 rounded-none">
                <AvatarImage src={recipientAvatar} />
                <AvatarFallback className="rounded-none text-[8px]">
                  {getInitials(recipientName)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Incoming Call Banner ────────────────────────────────────────────────────
// Shown in chat-window when another user is calling you.

interface IncomingCallBannerProps {
  call: CallDoc;
  ringtonePrefs?: RingtonePrefs;
  onAnswer: (callId: string) => void;
  onDecline: (callId: string) => void;
}

export function IncomingCallBanner({
  call,
  ringtonePrefs,
  onAnswer,
  onDecline,
}: IncomingCallBannerProps) {
  // Play ringtone while this banner is mounted (incoming ringing)
  useEffect(() => {
    startRingtone(ringtonePrefs ?? DEFAULT_RINGTONE_PREFS);
    return () => { stopRingtone(); };
  }, [ringtonePrefs]);

  // Auto-expire check
  useEffect(() => {
    const interval = setInterval(() => {
      if (isCallExpired(call, 30000)) {
        markCallMissed(call.id);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [call]);

  return (
    <div className="border-b bg-gradient-to-r from-green-500/15 to-green-500/5 animate-in slide-in-from-top-2 duration-300">
      <div className="flex items-center gap-3 px-3 py-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <Avatar className="h-10 w-10 rounded-full ring-2 ring-green-500/40 ring-offset-1 ring-offset-background">
            <AvatarImage src={call.callerAvatar} />
            <AvatarFallback className="text-xs rounded-full">
              {getInitials(call.callerName)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 animate-ping" />
          <div className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{call.callerName}</p>
          <p className="text-xs text-muted-foreground">
            Incoming {call.type} call...
          </p>
        </div>

        {/* Accept / Decline */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="destructive"
            className="h-8 w-8 p-0 rounded-full"
            onClick={() => onDecline(call.id)}
            title="Decline"
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            className="h-8 w-8 p-0 rounded-full bg-green-600 hover:bg-green-500 text-white"
            onClick={() => onAnswer(call.id)}
            title="Answer"
          >
            <PhoneIncoming className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
